import React, { useEffect, useCallback, useRef } from "react";
import useScrollLock from "../../hooks/useScrollLock";
import styles from "./Modal.module.css";

/**
 * Modal base reutilizável — apenas overlay + container.
 * Cada modal define seu próprio conteúdo via children.
 *
 * Enquanto aberto, trava a rolagem do `<body>` (`useScrollLock`): sem isso a
 * página de fundo captura o scroll e conteúdo alto dentro do modal fica
 * inalcançável.
 *
 * Props:
 *   isOpen    {boolean}       — controla visibilidade
 *   onClose   {Function}      — callback ao fechar (ESC ou clique no overlay)
 *   children  {ReactNode}
 *   width     {string}        — largura do container (default: "480px")
 *   titleId   {string}        — id do elemento de título para aria-labelledby (opcional)
 *   closeOnOverlayClick {boolean} — opt-out do clique-fora (default: true).
 *     Passe `false` em fluxos com formulário longo, onde um clique acidental no
 *     overlay descartaria dados já digitados. ESC e o botão de fechar continuam
 *     valendo: são ações deliberadas do usuário.
 */
export default function Modal({
  isOpen,
  onClose,
  children,
  width = "480px",
  titleId,
  closeOnOverlayClick = true,
}) {
  const containerRef = useRef(null);

  useScrollLock(isOpen);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleKeyDown);
    // Move foco para o container ao abrir
    containerRef.current?.focus();
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        ref={containerRef}
        className={styles.container}
        style={{ maxWidth: width }}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}
